import {
  useMutation,
  useQuery,
  useQueryClient,
  type UseMutationOptions,
} from '@tanstack/react-query';
import { foundationApi } from '@/api/client';
import type { Envelope } from '@/api/envelope';

// ============================================
// Types  (ported from legacy @console v3 service)
// ============================================

export type TeamRole = 'owner' | 'member';
export type MemberStatus = 'active' | 'pending' | 'inactive';

export interface Team {
  id: string;
  team_name: string;
  team_description: string;
  team_type: string;
  team_slug: string;
  organization: string;
  active: boolean;
  owner: string;
  members_count?: number;
  shares_count?: number;
  created_at: string;
  updated_at: string;
  /** Current user's role in this team — derived from the list endpoint. */
  user_role?: TeamRole;
}

export interface Member {
  id: string;
  email: string;
  uid?: string;
  active: boolean;
  pending: boolean;
  /** Deny-by-default: member sees only resources explicitly shared with them. */
  restricted: boolean;
  /** Per-member accept link token. Owner-only (redacted for other members); set while pending. */
  invite_token?: string;
  roles: string[];
  permissions?: string[];
  invited_at?: string;
  joined_at?: string;
  created_at: string;
  updated_at: string;
}

export interface TeamShare {
  id: string;
  team_id: string;
  project_id?: string | null;
  member_id?: string | null;
  member_email?: string;
  service_id: string;
  service_name?: string;
  resource_type?: string | null;
  resource_id?: string | null;
  actions: string[];
  created_at: string;
  updated_at: string;
}

export interface InvitationLink {
  id: string;
  token: string;
  team_id: string;
  created_at: string;
  expires_at?: string;
}

export interface CreateTeamRequest {
  team_name: string;
  team_description?: string;
  team_type?: string;
}

export interface UpdateTeamRequest {
  team_name?: string;
  team_description?: string;
  team_type?: string;
  active?: boolean;
}

export interface InviteMembersRequest {
  emails: string[];
  roles?: string[];
  permissions?: string[];
  /** Deny-by-default: new member sees only explicitly shared resources (no org auto-grant). */
  restricted?: boolean;
}

export interface UpdateMemberRequest {
  roles?: string[];
  permissions?: string[];
  active?: boolean;
  restricted?: boolean;
}

export interface CreateTeamShareRequest {
  service_id: string;
  actions: string[];
  /** Undefined = organization-wide; a project id scopes the grant to that project. */
  project_id?: string;
  member_id?: string;
  resource_type?: string;
  resource_id?: string;
}

export interface UpdateTeamShareRequest {
  actions?: string[];
  resource_type?: string;
  resource_id?: string;
}

export type PermissionAction = 'all' | 'list' | 'show' | 'create' | 'edit' | 'delete' | 'report';

export const PERMISSION_ACTIONS: {
  key: PermissionAction;
  label: string;
  description: string;
}[] = [
  { key: 'all', label: 'Full Access', description: 'Complete control over all operations' },
  { key: 'list', label: 'List', description: 'View the list of resources' },
  { key: 'show', label: 'View', description: 'View resource details' },
  { key: 'create', label: 'Create', description: 'Create new resources' },
  { key: 'edit', label: 'Edit', description: 'Modify existing resources' },
  { key: 'delete', label: 'Delete', description: 'Remove resources' },
  // Exclusive read-only role: restricts the member to overview, analytics and
  // usage reports only — no domains, settings, or any management/write access.
  { key: 'report', label: 'Report (read-only)', description: 'View overview, analytics & usage only — no management' },
];

/** Derived status for a member row. */
export function memberStatus(m: Member): MemberStatus {
  if (m.pending) return 'pending';
  return m.active ? 'active' : 'inactive';
}

// ============================================
// Response envelope helpers
// ============================================
// `/me/teams` returns a flatter, differently-named shape than `Team`.
interface UserTeamInfo {
  team_id: string;
  team_name: string;
  member_id?: string;
  role: string;
  owner: string;
  active: boolean;
  pending?: boolean;
  team_type?: string;
  members_count?: number;
  created_at?: string;
}

function transformUserTeam(t: UserTeamInfo): Team {
  return {
    id: t.team_id,
    team_name: t.team_name,
    team_description: '',
    team_type: t.team_type ?? '',
    team_slug: '',
    organization: '',
    active: t.active,
    owner: t.owner,
    members_count: t.members_count ?? 0,
    created_at: t.created_at ?? '',
    updated_at: '',
    user_role: t.role === 'owner' ? 'owner' : 'member',
  };
}

// ============================================
// Query keys
// ============================================

export const teamKeys = {
  all: ['v3', 'teams'] as const,
  myTeams: ['v3', 'me', 'teams'] as const,
  team: (id: string) => ['v3', 'teams', id] as const,
  members: (id: string) => ['v3', 'teams', id, 'members'] as const,
  shares: (id: string) => ['v3', 'teams', id, 'shares'] as const,
  invitationLink: (id: string) => ['v3', 'teams', id, 'invitation'] as const,
  myInvitations: ['v3', 'me', 'invitations'] as const,
};

export interface PendingInvitation {
  id: string;
  team_id: string;
  team_name: string;
  team_owner?: string;
  roles?: string[];
  invited_at?: string | null;
}

// ============================================
// Invitation detail types (GET /me/invitations/:member_id)
// ============================================

export interface ServicePermission {
  service_level?: string[];
  resources?: Record<string, {
    type_level?: string[];
    specific?: Record<string, string[]>;
  }>;
}

export interface MemberPermissionTree {
  member_id: string;
  permissions: Record<string, ServicePermission>;
}

export interface InvitationDetail {
  /** Same as member_id. */
  id: string;
  email: string;
  team_id: string;
  team_name: string;
  team_owner?: string;
  roles?: string[];
  permissions?: MemberPermissionTree;
  service_names?: Record<string, string>;
}

/** Response shape from POST /teams/validate. */
export interface TokenValidation {
  valid: boolean;
  team_name: string;
  team_id: string;
  owner: string;
  member?: { id: string; email: string; pending: boolean };
  message: string;
}

// ============================================
// Query hooks
// ============================================

/**
 * All teams the current user owns or belongs to. Excludes pending invitations
 * and soft-deleted teams (the backend returns inactive owned teams too).
 */
export function useMyTeams(options?: { enabled?: boolean }) {
  return useQuery({
    queryKey: teamKeys.myTeams,
    enabled: options?.enabled ?? true,
    queryFn: async () => {
      const res = await foundationApi.get<Envelope<{ teams: UserTeamInfo[] }>>('/me/teams');
      const teams = res.data?.data?.teams ?? [];
      return teams
        .filter((t) => !t.pending && t.active !== false)
        .map(transformUserTeam);
    },
  });
}

/** The current user's pending team invitations (across orgs). */
export function useMyInvitations() {
  return useQuery<PendingInvitation[]>({
    queryKey: teamKeys.myInvitations,
    staleTime: 30_000,
    queryFn: async () => {
      const res = await foundationApi.get<Envelope<{ invitations?: PendingInvitation[] }>>(
        '/me/invitations',
      );
      return res.data?.data?.invitations ?? [];
    },
  });
}

/** Fetch the full invitation detail for the current user. `enabled` when memberId is set. */
export function useInvitationDetail(memberId?: string) {
  return useQuery({
    queryKey: ['v3', 'me', 'invitations', memberId ?? ''],
    enabled: !!memberId,
    retry: false,
    queryFn: async (): Promise<InvitationDetail> => {
      const res = await foundationApi.get<Envelope<InvitationDetail>>(
        `/me/invitations/${memberId}`,
      );
      return res.data.data;
    },
  });
}

export function useTeam(id: string | undefined) {
  return useQuery({
    queryKey: teamKeys.team(id ?? ''),
    enabled: !!id,
    retry: false,
    queryFn: async (): Promise<Team | null> => {
      try {
        const res = await foundationApi.get<Envelope<{ team?: Team } & Team>>(`/teams/${id}`);
        const d = res.data?.data;
        return d?.team ?? (d as Team) ?? null;
      } catch (error) {
        const status = (error as { response?: { status?: number } })?.response?.status;
        // No access / not found are handled gracefully in the UI.
        if (status === 403 || status === 404) return null;
        throw error;
      }
    },
  });
}

export function useTeamMembers(teamId: string | undefined) {
  return useQuery({
    queryKey: teamKeys.members(teamId ?? ''),
    enabled: !!teamId,
    queryFn: async () => {
      const res = await foundationApi.get<Envelope<{ members?: Member[]; teammates?: Member[] }>>(
        `/teams/${teamId}/teammates`,
      );
      const d = res.data?.data;
      return d?.members ?? d?.teammates ?? [];
    },
  });
}

export function useTeamShares(teamId: string | undefined) {
  return useQuery({
    queryKey: teamKeys.shares(teamId ?? ''),
    enabled: !!teamId,
    queryFn: async () => {
      const res = await foundationApi.get<Envelope<{ shares?: TeamShare[] }>>(
        `/teams/${teamId}/permissions`,
      );
      return res.data?.data?.shares ?? [];
    },
  });
}

/** The team's active invitation link (owner-only). Returns null when none exists. */
export function useTeamInvitationLink(teamId: string | undefined, enabled = true) {
  return useQuery({
    queryKey: teamKeys.invitationLink(teamId ?? ''),
    enabled: !!teamId && enabled,
    retry: false,
    queryFn: async (): Promise<InvitationLink | null> => {
      try {
        const res = await foundationApi.get<Envelope<{ invitation?: InvitationLink } & InvitationLink>>(
          `/teams/${teamId}/invitation`,
        );
        const d = res.data?.data;
        return d?.invitation ?? (d as InvitationLink) ?? null;
      } catch (error) {
        const status = (error as { response?: { status?: number } })?.response?.status;
        if (status === 403 || status === 404) return null;
        throw error;
      }
    },
  });
}

// ============================================
// Mutation hooks
// ============================================

/** Invalidate the my-teams list + a single team's caches. */
function useTeamInvalidation() {
  const qc = useQueryClient();
  return {
    list: () => qc.invalidateQueries({ queryKey: teamKeys.myTeams }),
    team: (id: string) => qc.invalidateQueries({ queryKey: teamKeys.team(id) }),
    members: (id: string) => qc.invalidateQueries({ queryKey: teamKeys.members(id) }),
    shares: (id: string) => qc.invalidateQueries({ queryKey: teamKeys.shares(id) }),
    invitationLink: (id: string) =>
      qc.invalidateQueries({ queryKey: teamKeys.invitationLink(id) }),
  };
}

export function useCreateTeam(
  options?: UseMutationOptions<Team, unknown, CreateTeamRequest>,
) {
  const inv = useTeamInvalidation();
  return useMutation({
    mutationFn: async (body: CreateTeamRequest) => {
      const res = await foundationApi.post<Envelope<{ team?: Team } & Team>>('/teams', body);
      const d = res.data.data;
      return d?.team ?? (d as Team);
    },
    ...options,
    onSuccess: (...args) => {
      inv.list();
      options?.onSuccess?.(...args);
    },
  });
}

export function useUpdateTeam(
  id: string,
  options?: UseMutationOptions<Team, unknown, UpdateTeamRequest>,
) {
  const inv = useTeamInvalidation();
  return useMutation({
    mutationFn: async (body: UpdateTeamRequest) => {
      const res = await foundationApi.put<Envelope<{ team?: Team } & Team>>(`/teams/${id}`, body);
      const d = res.data.data;
      return d?.team ?? (d as Team);
    },
    ...options,
    onSuccess: (...args) => {
      inv.list();
      inv.team(id);
      options?.onSuccess?.(...args);
    },
  });
}

export function useDeleteTeam(
  options?: UseMutationOptions<void, unknown, string>,
) {
  const inv = useTeamInvalidation();
  return useMutation({
    mutationFn: async (id: string) => {
      await foundationApi.delete(`/teams/${id}`);
    },
    ...options,
    onSuccess: (...args) => {
      inv.list();
      options?.onSuccess?.(...args);
    },
  });
}

export function useInviteMembers(
  teamId: string,
  options?: UseMutationOptions<unknown, unknown, InviteMembersRequest>,
) {
  const inv = useTeamInvalidation();
  return useMutation({
    mutationFn: async (body: InviteMembersRequest) => {
      const res = await foundationApi.post(`/teams/${teamId}/teammates`, body);
      return res.data?.data;
    },
    ...options,
    onSuccess: (...args) => {
      inv.members(teamId);
      inv.team(teamId);
      options?.onSuccess?.(...args);
    },
  });
}

export function useUpdateMember(
  teamId: string,
  options?: UseMutationOptions<
    Member,
    unknown,
    { memberId: string; data: UpdateMemberRequest }
  >,
) {
  const inv = useTeamInvalidation();
  return useMutation({
    mutationFn: async ({ memberId, data }) => {
      const res = await foundationApi.put<Envelope<{ member?: Member } & Member>>(
        `/teams/${teamId}/teammates/${memberId}`,
        data,
      );
      const d = res.data.data;
      return d?.member ?? (d as Member);
    },
    ...options,
    onSuccess: (...args) => {
      inv.members(teamId);
      options?.onSuccess?.(...args);
    },
  });
}

export function useRemoveMember(
  teamId: string,
  options?: UseMutationOptions<void, unknown, string>,
) {
  const inv = useTeamInvalidation();
  return useMutation({
    mutationFn: async (memberId: string) => {
      await foundationApi.delete(`/teams/${teamId}/teammates/${memberId}`);
    },
    ...options,
    onSuccess: (...args) => {
      inv.members(teamId);
      inv.team(teamId);
      options?.onSuccess?.(...args);
    },
  });
}

/** Resend the invitation email to a pending member. `mutate(memberId)`. */
export function useResendInvitation(
  teamId: string,
  options?: UseMutationOptions<unknown, unknown, string>,
) {
  const inv = useTeamInvalidation();
  return useMutation({
    mutationFn: async (memberId: string) => {
      const res = await foundationApi.post(`/teams/${teamId}/teammates/${memberId}/invitation`);
      return res.data?.data;
    },
    ...options,
    onSuccess: (...args) => {
      inv.members(teamId);
      options?.onSuccess?.(...args);
    },
  });
}

export function useGenerateInvitationLink(
  teamId: string,
  options?: UseMutationOptions<InvitationLink, unknown, void>,
) {
  const inv = useTeamInvalidation();
  return useMutation({
    mutationFn: async () => {
      const res = await foundationApi.post<Envelope<{ invitation?: InvitationLink } & InvitationLink>>(
        `/teams/${teamId}/invitation`,
      );
      const d = res.data.data;
      return d?.invitation ?? (d as InvitationLink);
    },
    ...options,
    onSuccess: (...args) => {
      inv.invitationLink(teamId);
      options?.onSuccess?.(...args);
    },
  });
}

export function useRevokeInvitationLink(
  teamId: string,
  options?: UseMutationOptions<void, unknown, void>,
) {
  const inv = useTeamInvalidation();
  return useMutation({
    mutationFn: async () => {
      await foundationApi.delete(`/teams/${teamId}/invitation`);
    },
    ...options,
    onSuccess: (...args) => {
      inv.invitationLink(teamId);
      options?.onSuccess?.(...args);
    },
  });
}

export function useCreateTeamShare(
  teamId: string,
  options?: UseMutationOptions<TeamShare, unknown, CreateTeamShareRequest>,
) {
  const inv = useTeamInvalidation();
  return useMutation({
    mutationFn: async (body: CreateTeamShareRequest) => {
      const res = await foundationApi.post<Envelope<{ share?: TeamShare } & TeamShare>>(
        `/teams/${teamId}/permissions`,
        body,
      );
      const d = res.data.data;
      return d?.share ?? (d as TeamShare);
    },
    ...options,
    onSuccess: (...args) => {
      inv.shares(teamId);
      options?.onSuccess?.(...args);
    },
  });
}

export function useUpdateTeamShare(
  teamId: string,
  options?: UseMutationOptions<
    TeamShare,
    unknown,
    { shareId: string; data: UpdateTeamShareRequest }
  >,
) {
  const inv = useTeamInvalidation();
  return useMutation({
    mutationFn: async ({ shareId, data }) => {
      const res = await foundationApi.put<Envelope<{ share?: TeamShare } & TeamShare>>(
        `/teams/${teamId}/permissions/${shareId}`,
        data,
      );
      const d = res.data.data;
      return d?.share ?? (d as TeamShare);
    },
    ...options,
    onSuccess: (...args) => {
      inv.shares(teamId);
      options?.onSuccess?.(...args);
    },
  });
}

/** Delete a team-wide permission (share). `mutate(shareId)`. */
export function useDeleteTeamShare(
  teamId: string,
  options?: UseMutationOptions<void, unknown, string>,
) {
  const inv = useTeamInvalidation();
  return useMutation({
    mutationFn: async (shareId: string) => {
      await foundationApi.delete(`/teams/${teamId}/permissions/${shareId}`);
    },
    ...options,
    onSuccess: (...args) => {
      inv.shares(teamId);
      options?.onSuccess?.(...args);
    },
  });
}

// ============================================
// Invitation accept flow
// ============================================

/**
 * Validate an invitation token (POST /teams/validate).
 * Returns token validity + team info; any API error is treated as invalid.
 */
export function useValidateToken(token: string | undefined) {
  return useQuery({
    queryKey: ['v3', 'teams', 'validate', token ?? ''],
    enabled: !!token,
    retry: false,
    staleTime: Infinity,
    queryFn: async (): Promise<TokenValidation> => {
      try {
        const res = await foundationApi.post<Envelope<TokenValidation>>('/teams/validate', { token });
        return res.data.data;
      } catch {
        return {
          valid: false,
          team_name: '',
          team_id: '',
          owner: '',
          message: 'Invalid or expired invitation.',
        };
      }
    },
  });
}

/** Accept an invitation for the currently logged-in user. Body: `{token}` or `{member_id}`. */
export function useAcceptInvitation(
  options?: UseMutationOptions<unknown, unknown, { token?: string; member_id?: string }>,
) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (body: { token?: string; member_id?: string }) => {
      const res = await foundationApi.post('/teams/accepted', body);
      return res.data?.data;
    },
    ...options,
    onSuccess: (...args) => {
      qc.invalidateQueries({ queryKey: teamKeys.myInvitations });
      qc.invalidateQueries({ queryKey: teamKeys.myTeams });
      options?.onSuccess?.(...args);
    },
  });
}

/** Join a team as a newly registered user. Body: `{token, email, uid}`. */
export function useJoinTeam(
  options?: UseMutationOptions<unknown, unknown, { token: string; email: string; uid: string }>,
) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (body: { token: string; email: string; uid: string }) => {
      const res = await foundationApi.post('/teams/join', body);
      return res.data?.data;
    },
    ...options,
    onSuccess: (...args) => {
      qc.invalidateQueries({ queryKey: teamKeys.myInvitations });
      qc.invalidateQueries({ queryKey: teamKeys.myTeams });
      options?.onSuccess?.(...args);
    },
  });
}
